import { Clock, ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { AsyncSelect } from "@/components/ui/async-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type Salesperson,
  useSalespeople,
  useSalesperson,
} from "@/hooks/api/use-salespeople";
import {
  type RecentSalesperson,
  useSalespersonSelection,
} from "@/hooks/use-salesperson-selection";

// Union type to handle both full salespeople and recent salespeople
type SalespersonOption = Salesperson | RecentSalesperson;

// Helper to determine if an option is a recent salesperson (has timestamp)
const isRecentSalesperson = (
  salesperson: SalespersonOption,
): salesperson is RecentSalesperson => {
  return "timestamp" in salesperson;
};

/**
 * Renders a salesperson option with name, team, and a link to open in a new tab
 */
const renderSalespersonOption = (salesperson: SalespersonOption) => (
  <div className="flex items-center justify-between w-full group">
    <div className="flex items-center gap-2 flex-1">
      {isRecentSalesperson(salesperson) && (
        <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      )}
      <div className="flex flex-col min-w-0">
        <div className="font-medium">{salesperson.name}</div>
        <div className="text-xs text-muted-foreground">
          {salesperson.team_name && (
            <span>
              {salesperson.team_name}
              {salesperson.current_title && ` • ${salesperson.current_title}`}
            </span>
          )}
        </div>
      </div>
    </div>
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={`/salespeople/${salesperson.person_id}/explorer`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(
              `/salespeople/${salesperson.person_id}/explorer`,
              "_blank",
            );
          }}
          className="ml-2"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={5}>
        <p>Open in new tab</p>
      </TooltipContent>
    </Tooltip>
  </div>
);

/**
 * Salesperson selector component
 *
 * Allows searching and selecting salespeople with a dropdown.
 * Shows recent salespeople and search results.
 */
export function SalespersonSelector() {
  const [search, setSearch] = useState<string>();
  const {
    selectedPersonId,
    initialSalesperson,
    handleSalespersonChange,
    recentSalespeople,
  } = useSalespersonSelection();

  // Fetch salespeople based on search
  const { data: searchData, isFetching } = useSalespeople({
    search,
  });

  // Fetch selected salesperson details if not found in recent salespeople
  const { data: salespersonData } = useSalesperson(
    !initialSalesperson && selectedPersonId ? selectedPersonId : "",
  );

  // Determine which salesperson to show as selected
  const selectedSalesperson = initialSalesperson ?? salespersonData;

  // Add the selected salesperson to recent salespeople when coming from a URL
  useEffect(() => {
    if (!initialSalesperson && selectedPersonId && selectedSalesperson) {
      // Just add to recent without re-navigating
      handleSalespersonChange(
        selectedSalesperson.person_id,
        selectedSalesperson,
      );
    }
  }, [
    initialSalesperson,
    selectedPersonId,
    selectedSalesperson,
    handleSalespersonChange,
  ]);

  // Fetch handler for AsyncSelect component
  const fetcher = useCallback(
    async (
      searchValue?: string,
    ): Promise<{ results: SalespersonOption[]; hasMore: boolean }> => {
      // Update search value if it changed
      if (searchValue !== search) {
        setSearch(searchValue || undefined);
      }

      // Determine what to show based on search and results
      if (searchValue && searchData?.results?.length) {
        // Show search results
        return {
          results: searchData?.results ?? [],
          hasMore: false,
        };
      }

      // If we're searching but don't have results yet, return empty array
      // but the AsyncSelect will keep showing previous results until new ones arrive
      if (searchValue && isFetching) {
        return {
          results: [],
          hasMore: false,
        };
      }

      return {
        results: recentSalespeople,
        hasMore: false,
      };
    },
    [search, searchData, recentSalespeople, isFetching],
  );

  return (
    <AsyncSelect<SalespersonOption>
      fetcher={fetcher}
      preload={true}
      renderOption={renderSalespersonOption}
      getOptionValue={(salesperson) => salesperson.person_id}
      getDisplayValue={(salesperson) => salesperson.name}
      selectedOption={selectedSalesperson}
      label="Salesperson"
      placeholder="Select salesperson..."
      searchPlaceholder="Search by name or email"
      value={selectedPersonId ?? ""}
      onChange={handleSalespersonChange}
      loadThreshold={0.5}
      isSearching={!!search && isFetching}
      triggerClassName="bg-white dark:bg-[hsl(195_30%_12%)]"
      notFound={
        <div className="py-6 text-center text-sm">
          {isFetching ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching salespeople...</span>
            </div>
          ) : search ? (
            "No salespeople found"
          ) : (
            "No recent salespeople"
          )}
        </div>
      }
      loadingSkeleton={
        <div className="p-4">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col space-y-1">
                <div className="h-4 w-3/5 animate-pulse rounded bg-muted" />
                <div className="h-3 w-2/5 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
